import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button } from '@components/Button/Button';
import { ITableColumn } from '@components/common/UTable/UTable';
import { Popover } from 'antd';
import _ from 'lodash';

import { TransformedDictionaries } from '@/api/dictionaries/types';
import { ProtocolIndividual, TaskDrug } from '@/pages/Vaccination/components/task/types';
import { DOSAGE_UNIT_LABELS_SHORT } from '@/shared/pharmacy/enum';
import {
  individualVaccinationStatus,
  VACCINATION_STATUS,
} from '@/shared/vaccination/enums';

import cl from './styles.module.scss';

export const drugColumns = (withoutShortage: boolean, drugs?: TaskDrug[]) => {
  const sumMap: Record<string, number> = {};
  const firstIndexMap: Record<string, number> = {};

  if (drugs) {
    const grouped = _.groupBy(drugs, 'drugId');
    Object.entries(grouped).forEach(([drugId, items]) => {
      sumMap[drugId] = _.sumBy(items, 'usedVolume');
      firstIndexMap[drugId] = drugs.findIndex((item) => item.drugId === drugId);
    });
  }

  return [
    {
      title: '№ п/п',
      key: 'index',
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Препараты согласно наряду',
      dataIndex: 'name',
      width: 400,
    },
    {
      title: 'Номер партии',
      dataIndex: 'batchNumber',
      width: 170,
    },
    {
      title: 'Дата конца срока годности партии',
      dataIndex: 'expirationDate',
      width: 170,
      type: 'date',
    },
    {
      title: 'Необходимый объем препарата для наряда',
      dataIndex: 'usedVolume',
      width: 170,
      render: (_value: number, record: any, index: number) => {
        if (firstIndexMap[record.drugId] === index) {
          return sumMap[record.drugId];
        }
        return '';
      },
      onCell: (record: any, index?: number) => {
        if (!drugs) return {};

        const groupItems = drugs.filter((item) => item.drugId === record.drugId);
        const isFirstInGroup = firstIndexMap[record.drugId] === index;

        if (isFirstInGroup) {
          return {
            rowSpan: groupItems.length,
          };
        }
        return {
          rowSpan: 0,
        };
      },
    },
    {
      title: 'Количество необходимых ед. тары',
      dataIndex: 'packagesAmount',
      width: 170,
    },
    {
      title: 'Объем/доза препарата на единицу тары',
      dataIndex: 'packageVolume',
      width: 170,
      render: (value: any, record: any) => (
        <>
          {value}
          {record?.dosageUnit
            ? ' ' +
              DOSAGE_UNIT_LABELS_SHORT[
                record.dosageUnit as keyof typeof DOSAGE_UNIT_LABELS_SHORT
              ]
            : ''}
        </>
      ),
    },
    {
      title: 'Отсутствующий объем препарата для полного наряда',
      dataIndex: 'shortageVolume',
      className: 'warning-text',
      width: 170,
      hidden: withoutShortage,
      render: (_value: number, record: any, index: number) => {
        if (firstIndexMap[record.drugId] === index) {
          return record.shortageVolume;
        }
        return '';
      },
      onCell: (record: any, index?: number) => {
        if (!drugs) return {};

        const groupItems = drugs.filter((item) => item.drugId === record.drugId);
        const isFirstInGroup = firstIndexMap[record.drugId] === index;

        if (isFirstInGroup) {
          return {
            rowSpan: groupItems.length,
          };
        }
        return {
          rowSpan: 0,
        };
      },
    },
  ] as ITableColumn[];
};

export const columns = [
  {
    title: '№ п/п',
    dataIndex: 'orderNumber',
    width: 50,
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    width: 150,
  },
  {
    title: 'Инвентарный номер',
    dataIndex: 'inventoryNumber',
    width: 150,
  },
  {
    title: 'Дозировка',
    dataIndex: 'dosage',
    width: 100,
    render: (value: any, record: any) => (
      <>
        {value}
        {
          DOSAGE_UNIT_LABELS_SHORT[
            record?.dosageUnit as keyof typeof DOSAGE_UNIT_LABELS_SHORT
          ]
        }
      </>
    ),
  },
  {
    title: 'Объект',
    dataIndex: 'individualLocationId',
    width: 100,
  },
  {
    title: 'Расположение',
    dataIndex: 'location',
    width: 200,
  },
  {
    title: 'Дата рождения',
    dataIndex: 'birthDate',
    width: 150,
  },
] as ITableColumn[];

export const columnsNumber = (dictMap: TransformedDictionaries | undefined) => {
  return [
    {
      title: '1',
      dataIndex: 'orderNumber',
      width: 50,
      render: (value, record) =>
        record.status !== VACCINATION_STATUS.RESERVE ? value : '',
    },
    {
      title: '2',
      dataIndex: 'status',
      width: 150,
      render: (
        value: keyof typeof individualVaccinationStatus,
        record: ProtocolIndividual,
      ) => (
        <div className={cl.statusCell}>
          {individualVaccinationStatus[value]}
          {record?.individualDiseaseDescription && (
            <Popover
              classNames={{
                root: cl.popover,
              }}
              content={record.individualDiseaseDescription}
              placement="bottom"
              destroyOnHidden>
              <Button
                type="text"
                className={cl.btn}
                icon={<ExclamationCircleOutlined style={{ color: '#046094' }} />}
              />
            </Popover>
          )}
        </div>
      ),
    },
    {
      title: '3',
      dataIndex: 'inventoryNumber',
      width: 150,
    },
    {
      title: '4',
      dataIndex: 'dosage',
      width: 100,
      render: (value: number, record: any) =>
        value == null
          ? '-'
          : Number(value.toFixed(3)) +
            (DOSAGE_UNIT_LABELS_SHORT[
              record?.dosageUnit as keyof typeof DOSAGE_UNIT_LABELS_SHORT
            ] ?? ''),
    },
    {
      title: '5',
      dataIndex: 'individualLocationId',
      width: 100,
      render: (value: string) => dictMap?.INDIVIDUAL_LOCATION?.[value]?.name || '',
    },
    {
      title: '6',
      dataIndex: 'location',
      width: 200,
      render: (value: string) => value || '-',
    },
    {
      title: '7',
      dataIndex: 'birthDate',
      type: 'date',
      width: 150,
    },
  ] as ITableColumn[];
};
